import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import couplePhoto1 from "@/assets/testimonial-couple-1.jpg";
import couplePhoto2 from "@/assets/testimonial-couple-2.jpg";
import couplePhoto3 from "@/assets/testimonial-couple-3.jpg";
import couplePhoto4 from "@/assets/testimonial-couple-4.jpg";

const testimonials = [
  {
    quote: "We were on a tight budget but Pixie Clicks made us feel like we had a luxury team. Our photos look like they cost twice as much!",
    author: "Jessica & Michael",
    venue: "The Ashford Estate, NJ",
    highlight: "Half the price, double the quality",
    photo: couplePhoto1,
  },
  {
    quote: "The payment plan made it so easy to afford everything we wanted. No stress, no compromises. Best decision we made for our wedding.",
    author: "Sarah & David",
    venue: "Park Chateau, NJ",
    highlight: "Payment plan made it possible",
    photo: couplePhoto2,
  },
  {
    quote: "Every other vendor quoted us $8K+. Pixie Clicks gave us the same quality for so much less. Our highlight film still makes us cry!",
    author: "Emily & James",
    venue: "Nanina's in the Park, NJ",
    highlight: "Saved thousands without sacrificing quality",
    photo: couplePhoto3,
  },
  {
    quote: "As a budget-conscious bride, I was skeptical at first. But they exceeded every expectation. Our guests thought we hired a celebrity photographer!",
    author: "Amanda & Chris",
    venue: "The Manor, NJ",
    highlight: "Budget-friendly luxury experience",
    photo: couplePhoto4,
  },
];

export function TestimonialsCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);

  return (
    <section className="section-padding">
      <div className="container-narrow">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
            Real Couples, Real Budgets
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            See why budget-conscious couples choose us for their special day.
          </p>
        </div>

        <div className="relative mt-12">
          <div className="relative overflow-hidden rounded-2xl bg-card border border-border p-8 md:p-12">
            <Quote className="absolute left-6 top-6 h-12 w-12 text-primary/20" />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 text-center"
              >
                {/* Highlight Badge */}
                <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-6">
                  {testimonials[current].highlight}
                </span>
                
                <p className="font-serif text-xl leading-relaxed text-foreground md:text-2xl">
                  "{testimonials[current].quote}"
                </p>
                
                {/* Couple Photo and Info */}
                <div className="mt-8 flex flex-col items-center gap-3">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="relative"
                  >
                    <img
                      src={testimonials[current].photo}
                      alt={testimonials[current].author}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary/30 shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground text-xs">✓</span>
                    </div>
                  </motion.div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {testimonials[current].author}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonials[current].venue}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={prev}
              className="rounded-full border border-border bg-card p-2 text-foreground transition-colors hover:border-primary hover:text-primary"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrent(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === current
                      ? "w-6 bg-primary"
                      : "w-2 bg-border hover:bg-primary/50"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="rounded-full border border-border bg-card p-2 text-foreground transition-colors hover:border-primary hover:text-primary"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
